import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, FileOutlined, SolutionOutlined } from "@ant-design/icons";
import { Button, Card, Collapse, Descriptions, Divider, Empty, Input, List, Modal, Space, Spin, Tabs, Tag, Tooltip, Typography, message, notification,
} from "antd";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Swal from 'sweetalert2'; 

import { getApplicantByEmailApiRequest } from '../../../api/applicant'; 

import EnrollmentInfo from './EnrollmentInfo';
import PersonalInfo from './PersonalInfo';
import StatusInfo from './StatusInfo';

import { STATUS_COLORS } from "./utils"; 

const { Title, Text } = Typography;
const { Panel } = Collapse;


const StatusTag = memo(({ status }) => {
    const color = STATUS_COLORS[status?.toLowerCase()] || "default";
    return <Tag color={color}>{String(status || 'N/A').toUpperCase()}</Tag>;
});


const AdmissionApprovalModal = ({ visible, applicant, onClose, onStatusUpdate }) => {
    const [updateLoading, setUpdateLoading] = useState(false);
    
    const [fetchingDetails, setFetchingDetails] = useState(false);
    const [applicantDetails, setApplicantDetails] = useState(null);

    useEffect(() => {
        const fetchDetails = async (email) => {
            if (!email) return;

            setFetchingDetails(true);
            try {
                const response = await getApplicantByEmailApiRequest(email);
                if (response.success && response.data) {
                    setApplicantDetails(response.data);
                } else {
                    message.error(response.message || 'Failed to fetch applicant details');
                    setApplicantDetails(null);
                }
            } catch (error) {
                console.error('Error fetching applicant details for modal:', error);
                message.error('An error occurred while fetching applicant details.');
                setApplicantDetails(null);
            } finally {
                setFetchingDetails(false);
            }
        };

        if (visible && applicant?.email) {
            fetchDetails(applicant.email);
        }

        if (!visible) {
            setApplicantDetails(null);
        }
    }, [visible, applicant]);


    // This function is called after the user confirms the Swal dialog
    const handleActualAction = useCallback(async (newStatus, reason = null) => {
        setUpdateLoading(true);
        try {
            const dataForUpdate = {
                ...(reason && { admissionApprovalRejectMessage: reason }),
            };
            // Call the parent handler from ApplicationsDashboard
            await onStatusUpdate(applicant, newStatus, dataForUpdate);
        } catch (error) {
            notification.error({
                message: "Update Failed",
                description: error.message || "An unexpected error occurred.",
            });
        } finally {
            setUpdateLoading(false);
        }
    }, [applicant, onStatusUpdate]);

    // This function shows the Swal confirmation dialog
    const showConfirmAction = useCallback(async (newStatus) => {
        let confirmConfig = {
            title: `Confirm Action: ${newStatus}`,
            html: `
                <p>You are about to mark this admission as <strong>${newStatus.toUpperCase()}</strong>.</p>
                <p style="color: #dc3545; font-weight: bold; margin-top: 12px;">
                    This will notify the applicant and finalize this stage. Are you sure you want to proceed?
                </p>
            `,
            icon: newStatus === "Rejected" ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, ${newStatus}`,
            cancelButtonText: 'No, Cancel',
            confirmButtonColor: newStatus === "Rejected" ? '#dc3545' : '#52c41a',
            cancelButtonColor: '#6c757d',
        };

        if (newStatus === "Rejected") {
            confirmConfig.input = 'textarea';
            confirmConfig.inputPlaceholder = 'Please provide a clear reason for the rejection...';
            confirmConfig.inputValidator = (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Rejection reason must be at least 10 characters.';
                }
            };
        }

        const result = await Swal.fire(confirmConfig);
        if (result.isConfirmed) {
            const reason = newStatus === "Rejected" ? result.value : null;
            handleActualAction(newStatus, reason);
        }
    }, [handleActualAction]);

    const modalFooter = useMemo(() => {
        if (!applicantDetails) {
             return [<Button key="close" onClick={onClose}>Close</Button>];
        }
        
        const isAlreadyProcessed = ['Approved', 'Rejected'].includes(applicantDetails.admissionApprovalAdminStatus);

        return [
             <Space key="actions" size="middle" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    key="reject" 
                    danger 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => showConfirmAction("Rejected")} 
                    loading={updateLoading} 
                    disabled={isAlreadyProcessed} 
                    size="large"
                >
                    Reject Admission
                </Button>
                <Button 
                    key="approve" 
                    type="primary" 
                    icon={<CheckCircleOutlined />} 
                    onClick={() => showConfirmAction("Approved")} 
                    loading={updateLoading} 
                    disabled={isAlreadyProcessed} 
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }} 
                    size="large"
                >
                    Approve Admission
                </Button>
                <Button key="close" onClick={onClose} size="large" disabled={updateLoading}>
                    Close
                </Button>
            </Space>,
        ];
    }, [showConfirmAction, updateLoading, applicantDetails, onClose]);

    // Items for the embedded tabs
    const detailTabItems = useMemo(() => [
        {
            key: '1',
            label: 'Enrollment Info',
            children: applicantDetails ? <EnrollmentInfo data={applicantDetails} /> : <Spin />,
        },
        {
            key: '2',
            label: 'Applicant Info',
            children: applicantDetails ? <PersonalInfo data={applicantDetails} /> : <Spin />,
        },
        {
            key: '3',
            label: 'Status Summary',
            children: applicantDetails ? <StatusInfo data={applicantDetails} /> : <Spin />,
        },
    ], [applicantDetails]);

    const displayApplicant = applicantDetails || applicant;

    return (
        <>
            <Modal
                title={<div className="flex items-center"><SolutionOutlined className="mr-2 text-blue-500" /><span className="text-lg font-medium">Admission Approval Review</span></div>}
                open={visible}
                onCancel={onClose}
                footer={modalFooter}
                width={1100}
                centered
                destroyOnClose
            >
                <div className="p-4">
                    <Spin spinning={fetchingDetails} tip="Loading Full Applicant Details...">
                        <Card className="mb-5 border-l-4 shadow-sm" style={{ borderLeftColor: "#1890ff" }}>
                            <div className="text-center mb-2">
                                <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                    {displayApplicant?.firstName} {displayApplicant?.middleName} {displayApplicant?.lastName}
                                </Title>
                                <Tag color="blue" className="text-base px-4 py-1 mt-2">
                                    {displayApplicant?.applicantID}
                                </Tag>
                            </div>
                        </Card>

                        {applicantDetails ? (
                            <>
                                <Descriptions title={<Title level={5}>Admission Checklist</Title>} bordered size="middle" column={2} className="mb-5" labelStyle={{ fontWeight: "bold", width: '30%' }}>
                                    <Descriptions.Item label="Admission Requirements Status"><StatusTag status={applicantDetails.admissionRequirementsStatus} /></Descriptions.Item>
                                    <Descriptions.Item label="Exam & Interview Result"><StatusTag status={applicantDetails.approvedExamInterviewResult} /></Descriptions.Item>
                                    <Descriptions.Item label="Reservation Fee Status"><StatusTag status={applicantDetails.reservationFeePaymentStepStatus} /></Descriptions.Item>
                                    <Descriptions.Item label="Reservation Fee Paid (Optional)"><Text strong>₱{applicantDetails.reservationFeeAmountPaid || 0}</Text></Descriptions.Item>
                                </Descriptions>
                                
                                <Divider />

                                <Title level={5}>Submitted Admission Requirements</Title>
                                {applicantDetails.admissionRequirements && applicantDetails.admissionRequirements.length > 0 ? (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={applicantDetails.admissionRequirements}
                                        renderItem={(item, index) => {
                                            const isWaived = item.status?.toLowerCase() === 'waived';
                                            const hasViewableLink = !!item.imageUrl;

                                            return (
                                                <List.Item
                                                    key={item.fileName || index}
                                                    actions={
                                                        !isWaived && hasViewableLink
                                                            ? [
                                                                <Tooltip title="View File" key="view-tooltip">
                                                                    <Button
                                                                        type="text"
                                                                        icon={<EyeOutlined />}
                                                                        onClick={() => window.open(item.imageUrl, '_blank')}
                                                                    />
                                                                </Tooltip>
                                                            ]
                                                            : null
                                                    }
                                                >
                                                    <List.Item.Meta
                                                        avatar={<FileOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                                                        title={
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {!isWaived && hasViewableLink ? (
                                                                    <a href={item.imageUrl} target="_blank" rel="noopener noreferrer">
                                                                        {item.fileName || 'View Submitted File'}
                                                                    </a>
                                                                ) : (
                                                                    <span>{item.fileName || 'Requirement File'}</span>
                                                                )}
                                                                <StatusTag status={item.status} />
                                                            </div>
                                                        }
                                                        description={`Requirement: ${item.name || 'N/A'}`}
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                ) : (
                                    <Empty description="No admission requirements have been submitted." />
                                )}

                                <Divider />

                                <Collapse ghost>
                                    <Panel header={<Title level={5} style={{margin: 0}}>View Full Applicant Details</Title>} key="1">
                                        <Tabs defaultActiveKey="1" items={detailTabItems} type="card" />
                                    </Panel>
                                </Collapse>

                            </>
                        ) : (
                            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {fetchingDetails ? null : <Text type="secondary">Could not load details.</Text>}
                            </div>
                        )}
                    </Spin>
                </div>
            </Modal>
        </>
    );
};

export default memo(AdmissionApprovalModal);